import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Shield, Loader2, Key, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

export default function MFAVerification({ userId, tempToken, onSuccess, onBack }) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const handleVerify = async () => {
    if (!code) {
      toast.error('Please enter a code');
      return;
    }

    setVerifying(true);
    try {
      const endpoint = useRecoveryCode ? '/mfa/recovery' : '/mfa/verify-login';
      const response = await axios.post(`${API}${endpoint}`, {
        user_id: userId,
        code: code.replace(/[\s-]/g, ''),
        temp_token: tempToken
      });

      // Store token and user data
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      // Show warning if backup code was used
      if (response.data.backup_code_used) {
        toast.info(
          `Backup code used. ${response.data.remaining_backup_codes} codes remaining.`,
          { duration: 5000 }
        );
      }
      
      onSuccess(response.data);
    } catch (error) {
      console.error('MFA verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.length >= 6) {
      handleVerify();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-4">
              {useRecoveryCode ? (
                <Key className="w-8 h-8 text-teal-600" />
              ) : (
                <Shield className="w-8 h-8 text-teal-600" />
              )}
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              {useRecoveryCode ? 'Enter Recovery Code' : 'Two-Factor Authentication'}
            </h2>
            <p className="text-slate-600">
              {useRecoveryCode 
                ? 'Enter one of your backup recovery codes'
                : 'Enter the 6-digit code from your authenticator app'
              }
            </p>
          </div>

          {/* Code Input */}
          <div className="space-y-4">
            <Input
              value={code}
              onChange={(e) => {
                const value = useRecoveryCode 
                  ? e.target.value.toUpperCase().slice(0, 9)
                  : e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
              }}
              placeholder={useRecoveryCode ? "XXXX-XXXX" : "000000"}
              className="text-center text-2xl tracking-[0.5em] font-mono bg-slate-50 border-slate-300 h-14"
              onKeyPress={handleKeyPress}
              autoFocus
              data-testid="mfa-code-input"
            />

            <Button
              onClick={handleVerify}
              disabled={verifying || (useRecoveryCode ? code.length < 8 : code.length !== 6)}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-medium"
              data-testid="verify-mfa-login-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </div>

          {/* Toggle Recovery Code Mode */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setUseRecoveryCode(!useRecoveryCode);
                setCode('');
              }}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              data-testid="toggle-recovery-mode"
            >
              {useRecoveryCode 
                ? 'Use authenticator app instead'
                : "Can't access your authenticator? Use a recovery code"
              }
            </button>
          </div>

          {/* Back Button */}
          {onBack && (
            <div className="mt-4 text-center">
              <button
                onClick={onBack}
                className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to login
              </button>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-900 mb-2">Need help?</h4>
            <ul className="text-xs text-slate-600 space-y-1">
              <li>Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy)</li>
              <li>Find the entry for CyberForte</li>
              <li>Enter the 6-digit code shown</li>
              {!useRecoveryCode && (
                <li>If you don't have access to your device, use a recovery code</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
